"use client";

import React, { useState, useMemo } from "react";
import Select from "react-select";
import ScheduleModal from "./ScheduleModal";
import toast from "react-hot-toast";
import { Mail } from "lucide-react";
import { customStyles } from "../../../utils/index";
import EmailExcelModal from "./EmailExcelModal";
import { sendEmail } from "../../../services/emailSendServices";

const EmailMetaApiForm = ({ templates, groups, refreshSchedule }) => {
  const [sendTo, setSendTo] = useState("group");
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templateName, setTemplateName] = useState("");
  const [subject, setSubject] = useState("");
  const [sampleMessage, setSampleMessage] = useState("");
  const [templateParams, setTemplateParams] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState({
    value: "",
    label: "",
    totalUsers: 0,
  });
  const [isExcelDataLoaded, setIsExcelDataLoaded] = useState(false);
  const [templateType, setTemplateType] = useState("");
  const [name, setName] = useState("");
  const [email, setemail] = useState("");
  const [selectedSendOption, setSelectedSendOption] = useState("schedule");
  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);
  const [scheduleDate, setScheduleDate] = useState(null);
  const [isExcelModalOpen, setIsExcelModalOpen] = useState(false);
  const [excelData, setExcelData] = useState([]);

  const formattedScheduledTime = scheduleDate
    ? scheduleDate.toLocaleString("en-IN", {
        weekday: "long",
        day: "2-digit",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      })
    : "";

  const extractVariables = (message) => {
    const regex = /{{(.*?)}}/g;
    const matches = [...message.matchAll(regex)];
    const uniqueParams = new Set();

    matches.forEach((match) => {
      uniqueParams.add(match[1].trim());
    });

    return Array.from(uniqueParams).map((key) => ({
      key,
      value: "",
    }));
  };

  const replaceTemplateParams = (message, params) => {
    let result = message;

    params.forEach((param) => {
      const escapedKey = param.key.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
      const regex = new RegExp(`{{\\s*${escapedKey}\\s*}}`, "gi");
      if (param.value.trim()) {
        result = result.replace(regex, param.value.trim());
      } else {
        result = result.replace(
          regex,
          `<span style="color: red;">{{${param.key}}}</span>`
        );
      }
    });

    result = result.replace(
      /{{(.*?)}}/g,
      (_, key) => `<span style="color: red;">{{${key.trim()}}}</span>`
    );

    return result;
  };

  const displayMessage = useMemo(() => {
    if (isExcelDataLoaded && excelData.length > 0 && sendTo === "group") {
      const firstRow = excelData[0];
      const params = templateParams.map((param) => ({
        key: param.key,
        value: firstRow[param.key] || "",
      }));
      return replaceTemplateParams(sampleMessage, params);
    }
    return sampleMessage
      ? replaceTemplateParams(sampleMessage, templateParams)
      : "";
  }, [sampleMessage, templateParams, excelData, isExcelDataLoaded, sendTo]);

  const groupOptions = groups.map((g) => ({
    value: g.value,
    label: g.label,
    totalUsers: g.totalUsers ?? 0,
  }));

  const templateOptions = templates.map((t) => ({
    value: t._id,
    label: t.templateName,
  }));

  const handleParamChange = (index, value) => {
    const newParams = [...templateParams];
    newParams[index].value = value;
    setTemplateParams(newParams);
  };

  const filtered = groups.find((g) => g.value === selectedGroup.value);

  const handleExcelDataSubmit = (data) => {
    setExcelData(data);
    setIsExcelModalOpen(false);
  };

  const handleTemplateChange = async (option) => {
    setSelectedTemplate(option);
    if (!option?.value) return;

    try {
      const selected = templates.find((t) => t._id === option.value);

      if (!selected) {
        toast.error("Selected template not found");
        return;
      }

      setTemplateName(selected.templateName || "");
      setSampleMessage(selected.sampleMessage || "");
      setSubject(selected.subject || "");

      if (selected.sampleMessage) {
        setTemplateParams(extractVariables(selected.sampleMessage));
      } else {
        setTemplateParams([]);
      }
    } catch (err) {
      toast.error("Failed to fetch template details");
      setTemplateName("");
      setSampleMessage("");
      setTemplateParams([]);
    }
  };

  const handleSubmit = async () => {
    let to;
    let replyto = "dcoresms2@gmail.com";

    if (sendTo === "individual") {
      to = email?.trim();
    } else if (sendTo === "group") {
      const groupUsers = filtered?.users || [];

      const emails = groupUsers
        .map((user) => user.email?.trim())
        .filter((email) => !!email);
      to = emails.join(",");
    }

    // if (!to || !replyto || !displayMessage) {
    //   toast.error("Missing recipient emails or message");
    //   return;
    // }

    const payload = {
      to,
      replyto,
      message: displayMessage,
      subject,
    };

    try {
      toast.loading("Sending emails...");
      const response = await sendEmail(payload);
      toast.dismiss();
      toast.success(response.message || "Emails sent successfully");
      refreshSchedule?.();
    } catch (error) {
      toast.dismiss();
      toast.error(error.message || "Failed to send emails");
    }
  };

  return (
    <>
      <div
        style={{ boxShadow: "0px 0px 4px 0px #00000040" }}
        className=" w-full bg-white p-4 mt-4 rounded-2xl border border-gray-200 text-base font-medium text-black"
      >
        {/* Send To Type */}
        <div className="flex mb-3 justify-normal flex-col items-start relative w-full">
          <span className="pb-2">Send to</span>
          <div className="flex gap-5">
            {["group", "individual"].map((val) => (
              <label
                key={val}
                className="flex items-center cursor-pointer relative"
              >
                <input
                  type="radio"
                  name="group"
                  value={val}
                  checked={sendTo === val}
                  onChange={(e) => setSendTo(e.target.value)}
                  className="appearance-none sr-only"
                />
                <div className="w-5 h-5 absolute left-0 rounded-full bg-[#d9d9d9] flex items-center justify-center">
                  <div
                    className={`w-3 h-3 rounded-full transition ${
                      sendTo === val ? "bg-[#4362C1]" : "bg-[#C0C4D1]"
                    }`}
                  />
                </div>
                <span className="ml-2 text-sm font-medium text-black">
                  {val === "individual" ? "By Individual" : "By Group"}
                </span>
              </label>
            ))}
          </div>
        </div>

        {/* Group Select */}
        {sendTo === "group" && (
          <div className="mb-4 max-w-md">
            <Select
              options={groupOptions}
              value={selectedGroup.value ? selectedGroup : null}
              onChange={(option) => setSelectedGroup(option)}
              styles={customStyles}
              components={{ IndicatorSeparator: () => null }}
              placeholder="Select a group"
            />
            {filtered && (
              <span className="text-xs text-black font-normal mt-1">
                This group contains {filtered.totalUsers ?? 0} users{" "}
                <span className="text-blue underline cursor-pointer">
                  View list
                </span>
              </span>
            )}
          </div>
        )}

        {/* Individual Inputs */}
        {sendTo === "individual" && (
          <div className="space-y-3 mb-4 max-w-md">
            <input
              type="text"
              placeholder="Name (optional)"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 h-[40px] border border-[#0000003D]/[24%] placeholder:text-black rounded-lg text-sm focus:outline-none  focus:ring-1 focus:ring-blue"
            />
            <div className="relative">
              <div className="absolute left-3 top-1/2 transform -translate-y-1/2 flex items-center gap-2">
                <Mail color="#E0E0E0" size={20} />
                <div className="h-6 w-px bg-gray-300"></div>
              </div>
              <input
                type="tel"
                value={email}
                onChange={(e) => setemail(e.target.value)}
                placeholder="Email Id"
                className="w-full pl-12 pr-3 h-[40px] placeholder:text-black border border-[#0000003D]/[24%] rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue"
              />
            </div>
          </div>
        )}

        {/* Template Selection */}
        <div className=" mb-4">
          <label className="block text-sm mb-2">Select Template</label>
          <Select
            options={templateOptions}
            value={selectedTemplate}
            onChange={handleTemplateChange}
            className="max-w-md !text-sm"
            styles={customStyles}
            components={{ IndicatorSeparator: () => null }}
            placeholder="Search or select template"
          />

          {templateName && (
            <div className="mt-4 max-w-md">
              <label className="block text-sm mb-2">Template Name</label>
              <input
                type="text"
                value={templateName}
                readOnly
                className="w-full border border-[#0000003D] rounded-[10px] text-black font-normal px-3 h-10  !text-sm"
              />
            </div>
          )}

          {sampleMessage && (
            <div className="mt-4">
              <label className="block text-sm mb-2">Sample Message</label>
              <div className="w-full border py-6 border-[#0000003D] bg-[#FAFAFA] rounded-[10px] text-black font-normal px-3  text-sm">
                <div
                  className="prose-new max-w-none [&_h1]:text-xl [&_h1]:text-center [&_p]:text-center [&_p]:max-w-[600px] [&_p]:mx-auto
 [&_img.editor-image]:mx-auto [&_img.editor-image]:rounded-lg [&_img.editor-image]:max-w-full [&_img.editor-image]:h-auto [&_button.editor-button]:bg-[#4F22D8] [&_button.editor-button]:text-white [&_button.editor-button]:px-4 [&_button.editor-button]:py-2 [&_button.editor-button]:rounded-md [&_button.editor-button]:text-sm [&_button.editor-button]:font-medium [&_button.editor-button]:mx-auto [&_button.editor-button]:block"
                  dangerouslySetInnerHTML={{
                    __html: displayMessage,
                  }}
                />
              </div>
            </div>
          )}

          {/* Template Params for both types */}
          {templateParams.length > 0 && sendTo === "individual" && (
            <div className="mt-4 max-w-md">
              <label className="block text-sm mb-2">
                Fill Template Parameters ({templateType})
              </label>
              <div className="space-y-3">
                {templateParams.map((param, index) => (
                  <div key={param.key} className="flex flex-col">
                    <label className="text-sm font-medium mb-1">
                      {param.key}:
                    </label>
                    <input
                      type="text"
                      value={param.value}
                      onChange={(e) => handleParamChange(index, e.target.value)}
                      placeholder={`Enter value for ${param.key}`}
                      className="border border-[#0000003D] bg-white rounded-[10px] text-black font-normal px-3 h-10 text-sm"
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {templateParams.length > 0 && sendTo === "group" && (
            <div className="mt-4">
              <div
                onClick={() => setIsExcelModalOpen(true)}
                className="w-full bg-[#4F22D8] flex justify-center items-center text-white font-semibold py-2 rounded-md  transition"
              >
                Fill Parameters via Excel
              </div>
              {excelData.length > 0 && (
                <span className="text-xs text-green mt-4">
                  Excel data loaded for {excelData.length} users
                </span>
              )}
            </div>
          )}
        </div>

        {/* Schedule Section */}
        <div className="mb-4">
          <label className="block text-sm font-semibold mb-2">
            Schedule / Send
          </label>
          {["Schedule For Later", "Send Now"].map((val) => {
            const valueKey = val === "Schedule For Later" ? "schedule" : "now";
            return (
              <label key={valueKey} className="radio-item">
                <input
                  type="radio"
                  name="sendOption"
                  value={valueKey}
                  checked={selectedSendOption === valueKey}
                  onChange={(e) => setSelectedSendOption(e.target.value)}
                />
                <span className="radio-circle" />
                <span className="radio-label">{val}</span>
              </label>
            );
          })}
          {selectedSendOption === "schedule" && (
            <div
              className="mt-4 text-xs text-[#4F22D8] font-medium cursor-pointer"
              onClick={() => setIsScheduleModalOpen(true)}
            >
              {scheduleDate
                ? `Message Scheduled On ${formattedScheduledTime}`
                : "Select Date & Time"}
            </div>
          )}
        </div>

        <button
          onClick={handleSubmit}
          disabled={
            !selectedTemplate?.value ||
            (sendTo === "group" && !selectedGroup?.value) ||
            (sendTo === "individual" && !email)
          }
          className={`w-full bg-[#4F22D8] text-white font-semibold py-2 rounded-md transition ${
            !selectedTemplate?.value ||
            (sendTo === "group" && !selectedGroup?.value) ||
            (sendTo === "individual" && !email)
              ? "opacity-50 cursor-not-allowed"
              : "hover:bg-[#3b51a3] cursor-pointer"
          }`}
        >
          {selectedSendOption === "schedule" ? "Schedule Email" : "Send Email"}
        </button>
      </div>

      {/* Modals */}
      <ScheduleModal
        isOpen={isScheduleModalOpen}
        onClose={() => setIsScheduleModalOpen(false)}
        initialDate={scheduleDate}
        onConfirm={(date) => {
          setScheduleDate(date);
          setIsScheduleModalOpen(false);
        }}
      />

      <EmailExcelModal
        isOpen={isExcelModalOpen}
        onClose={() => setIsExcelModalOpen(false)}
        onSubmit={handleExcelDataSubmit}
        templateParams={templateParams}
        groupUsers={filtered?.users || []}
        setTemplateParams={setTemplateParams}
      />
    </>
  );
};

export default EmailMetaApiForm;
